import { distanceMi, type LatLon } from './geo';
import type { PlaceLookup } from './items';
import type { Item } from './schema';

/**
 * When I'm actually at each plan, as opposed to the times printed on the booking. A hotel's
 * 3 PM check-in doesn't mean I'm there at 3 if my flight lands at 9, and a 4 PM late checkout
 * doesn't mean I leave then if I fly out at 11. Flights, trains, buses, ferries and cruises
 * are fixed points; everything else bends around them using where it is:
 *
 * - Something near where a trip lands (and far from where it left) that starts before the
 *   landing, or during the trip, happens after it.
 * - Something near where a trip leaves (and far from where it lands) that ends after the
 *   departure, or during the trip, is over before it.
 *
 * On arrival it's the car or ride first, then the hotel, then anything else; on departure the
 * reverse. A stay that runs on through a side trip is left alone, since it only bends when the
 * conflicting trip is within a day of its own start or end.
 */

const MIN = 60_000;
const DAY = 24 * 60 * MIN;
/** Close enough to count as the same city. */
const NEAR_MI = 150;

export const FIXED_TYPES: ReadonlySet<Item['type']> = new Set([
  'flight',
  'train',
  'bus',
  'ferry',
  'cruise',
]);

export interface Span {
  start: number;
  end: number | null;
}

/** On arrival: pick up the car, then check in, then the rest. Leaving is the mirror image. */
function arriveOffset(type: Item['type']): number {
  if (type === 'car' || type === 'transfer' || type === 'parking') return 1 * MIN;
  if (type === 'lodging') return 2 * MIN;
  return 3 * MIN;
}
function leaveOffset(type: Item['type']): number {
  if (type === 'car' || type === 'transfer' || type === 'parking') return 60 * MIN;
  if (type === 'lodging') return 120 * MIN;
  return 180 * MIN;
}

interface Fixed {
  depart: number;
  arrive: number;
  /** Landed and, if there's one waiting, in the car or ride: when the rest can begin. */
  ready: number;
  /** Mirror of ready: the car or ride to the airport begins by then. */
  leaveBy: number;
  from: LatLon | null;
  to: LatLon | null;
}

const coords = (p: { lat: number | null; lon: number | null } | undefined): LatLon | null =>
  p?.lat != null && p.lon != null ? { lat: p.lat, lon: p.lon } : null;

/** True when `here` is at `a` and not at `b`. Unknown places never match. */
function nearOnly(here: LatLon | null, a: LatLon | null, b: LatLon | null): boolean {
  if (!here || !a) return false;
  if (distanceMi(here, a) > NEAR_MI) return false;
  return !b || distanceMi(here, b) > NEAR_MI;
}

/** Effective start and end of every dated plan that needed moving, by id. */
export function effectiveTimes(items: Item[], places: PlaceLookup): Map<string, Span> {
  const fixed: Fixed[] = [];
  for (const i of items) {
    if (!FIXED_TYPES.has(i.type) || i.startAt == null) continue;
    fixed.push({
      depart: i.startAt,
      arrive: i.endAt ?? i.startAt,
      ready: i.endAt ?? i.startAt,
      leaveBy: i.startAt,
      from: coords(places(i.startPlaceId)),
      to: coords(places(i.endPlaceId)),
    });
  }

  const ground = (t: Item['type']) => t === 'car' || t === 'transfer' || t === 'parking';
  // Cars and rides first, so a hotel can wait for the pickup.
  const order = items
    .filter((i) => !FIXED_TYPES.has(i.type) && i.startAt != null)
    .sort((a, b) => Number(ground(b.type)) - Number(ground(a.type)));
  const out = new Map<string, Span>();
  for (const i of order) {
    if (i.startAt == null) continue;
    const startAt = coords(places(i.startPlaceId));
    const endAt = coords(places(i.endPlaceId)) ?? startAt;
    let start = i.startAt;
    let end = i.endAt;

    // Arriving: it can't begin before I've got to its city.
    const window = end ?? start + DAY;
    const inTransit = fixed.find((f) => f.depart < start && start < f.arrive);
    const lastArrival = fixed
      .filter((f) => f.arrive <= start)
      .reduce<Fixed | null>((m, f) => (!m || f.arrive > m.arrive ? f : m), null);
    const elsewhere = inTransit
      ? !nearOnly(startAt, inTransit.from, null) || nearOnly(startAt, inTransit.to, inTransit.from)
      : lastArrival
        ? !!startAt && !!lastArrival.to && distanceMi(startAt, lastArrival.to) > NEAR_MI
        : false;
    const firstHere = fixed
      .filter((f) => f.arrive > start && f.arrive < window && nearOnly(startAt, f.to, f.from))
      .reduce<Fixed | null>((m, f) => (!m || f.arrive < m.arrive ? f : m), null);
    if (firstHere && (elsewhere || firstHere.arrive - start < DAY))
      start = firstHere.arrive + arriveOffset(i.type);
    if (ground(i.type)) {
      // A pickup soon after landing is how I leave the airport.
      for (const f of fixed)
        if (
          start >= f.arrive &&
          start - f.arrive <= 3 * 60 * MIN &&
          nearOnly(startAt, f.to, f.from)
        )
          f.ready = Math.max(f.ready, start);
    } else {
      for (const f of fixed)
        if (start >= f.arrive && start <= f.ready && nearOnly(startAt, f.to, f.from))
          start = f.ready + arriveOffset(i.type);
    }
    if (end != null) {
      // Leaving: it's over once I fly away from its city and don't come back before it ends.
      const e = end;
      const gone = fixed
        .filter(
          (f) =>
            f.depart > start &&
            f.depart < e &&
            nearOnly(endAt, f.from, f.to) &&
            !fixed.some(
              (g) => g.arrive > f.depart && g.arrive <= e && nearOnly(endAt, g.to, g.from),
            ),
        )
        .reduce<Fixed | null>((m, f) => (!m || f.depart < m.depart ? f : m), null);
      if (gone) end = Math.max(start, gone.depart - leaveOffset(i.type));
      if (ground(i.type)) {
        // Dropping the car shortly before a flight is how I get to the airport.
        for (const f of fixed)
          if (end <= f.depart && f.depart - end <= 3 * 60 * MIN && nearOnly(endAt, f.from, f.to))
            f.leaveBy = Math.min(f.leaveBy, end);
      } else {
        for (const f of fixed)
          if (
            end <= f.depart &&
            end >= f.leaveBy &&
            start < f.leaveBy &&
            nearOnly(endAt, f.from, f.to)
          )
            end = f.leaveBy - (leaveOffset(i.type) - leaveOffset('car'));
      }
      if (end < start) end = start;
    }
    if (start !== i.startAt || end !== i.endAt) out.set(i.id, { start, end });
  }
  return out;
}
